import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2 } from 'lucide-react';
import { logError } from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OnboardingPage = () => {
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSources();
  }, []);

  const fetchSources = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/sources`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch sources');
      }

      const data = await response.json();
      setSources(data);
    } catch (err) {
      logError('OnboardingPage:fetchSources', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleSource = (sourceId) => {
    setSelectedSources((prev) =>
      prev.includes(sourceId)
        ? prev.filter((id) => id !== sourceId)
        : [...prev, sourceId]
    );
  };

  const handleContinue = () => {
    // Mock onboarding completion
    alert(`You've selected ${selectedSources.length} sources. This will be saved in future versions.`);
    navigate('/');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading news sources...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-800 font-medium">Error loading sources</p>
          <p className="text-red-600 text-sm mt-2">{error}</p>
          <button
            onClick={fetchSources}
            className="mt-4 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="onboarding-page">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-5xl font-serif font-bold text-gray-900 mb-4">
          Choose Your Sources
        </h1>
        <p className="text-xl text-gray-600">
          Select the news outlets you want to follow. We'll help you see all perspectives.
        </p>
      </div>

      {/* Source Selection Grid */}
      <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
        <div className="mb-6">
          <p className="text-sm text-gray-600">
            {selectedSources.length} of {sources.length} sources selected
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[500px] overflow-y-auto pr-2">
          {sources.map((source) => (
            <div
              key={source.id}
              className="flex items-start space-x-3 p-4 border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 cursor-pointer transition-colors"
              onClick={() => toggleSource(source.id)}
              data-testid={`source-checkbox-${source.id}`}
            >
              <Checkbox
                checked={selectedSources.includes(source.id)}
                onCheckedChange={() => toggleSource(source.id)}
                className="mt-0.5"
              />
              <div className="flex-1 min-w-0">
                <label className="font-medium text-gray-900 cursor-pointer">
                  {source.name}
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center">
        <Button
          variant="outline"
          onClick={() => navigate('/')}
          data-testid="skip-button"
        >
          Skip for now
        </Button>
        <Button
          onClick={handleContinue}
          disabled={selectedSources.length === 0}
          data-testid="continue-button"
        >
          Continue with {selectedSources.length} sources
        </Button>
      </div>

      {/* Info */}
      <p className="text-center text-sm text-gray-500 mt-8">
        This is a UI mockup. Source preferences will be saved in future versions.
      </p>
    </div>
  );
};

export default OnboardingPage;
